import { descriptionSchema } from './patent-description'
import { stripHTML } from '../utils'
import { z } from 'zod'

const abstractParagraphSchema = z.object({
  dataFormat: z.string().optional(),
  lang: z.string(),
  loadSource: z.string().optional(),
  plainText: z.string().transform((abstract) => stripHTML(abstract, true)),
})

const claimSchema = z.object({
  claimId: z.string(),
  dataFormat: z.string().optional(),
  isDependent: z.boolean().optional().default(false),
  lang: z.string(),
  loadSource: z.string().optional(),
  plainText: z.string().transform((claim) => stripHTML(claim, true)),
  sequence: z.number(),
  wordCount: z.number().optional(),
})

const publicationReferenceSchema = z.object({
  country: z.string().optional(),
  documentDate: z.string().optional(),
  documentNumber: z.string(),
  kind: z.string().optional(),
})

const applicationReferencesSchema = z.object({
  documentNumber: z.string().optional(),
  documentDate: z.string().optional(),
  country: z.string().optional(),
  kind: z.string().optional(),
  dataFormat: z.string().optional(),
})

export type ApplicationReference = z.infer<typeof applicationReferencesSchema>

const citationSchema = z.object({
  citedPhase: z.string().optional(),
  documentNumber: z.string().optional(),
})

const claimsXmlSchema = z.object({
  claimId: z.string().optional(),
  dataFormat: z.string().optional(),
  isDependent: z.boolean().optional().default(false),
  lang: z.string().optional(),
  sequence: z.number().optional(),
  wordCount: z.number().optional(),
  xmlText: z.string().optional(),
})

const cpcClassificationSchema = z.object({
  actionDate: z.string().optional(),
  classificationDataSource: z.string().optional(),
  classificationStatus: z.string().optional(),
  classificationValue: z.string().optional(),
  generatingOffice: z.string().optional(),
  mainGroup: z.string().optional(),
  section: z.string().optional(),
  sequenceNumber: z.number().optional(),
  subClass: z.string().optional(),
  subGroup: z.string().optional(),
  symbol: z.string().optional(),
  symbolPosition: z.string().optional(),
  topClass: z.string().optional(),
})

const currentAssigneeSchema = z.object({
  partyName: z.string().optional(),
})

const currentOwnerSchema = z.object({
  partyName: z.string().optional(),
})

const inventionTitlesSchema = z.object({
  title: z.string().optional(),
  lang: z.string().optional(),
})

const inventorsSchema = z.object({
  dataFormat: z.string().optional(),
  partyName: z.string().optional(),
  partyNameClean: z.string().optional(),
  partyType: z.string().optional(),
})

const legalEventsSchema = z.object({
  englishDescription: z.string().optional(),
  eventCode: z.string().optional(),
  eventDate: z.string().optional(),
  eventDetails: z.object({}).optional(),
  eventEffectiveDate: z.string().optional(),
  legalEventId: z.number().optional(),
  sequenceNumber: z.number().optional(),
})

const priorityClaimsSchema = z.object({
  country: z.string().optional(),
  documentDate: z.string().optional(),
  documentNumber: z.string().optional(),
  kind: z.string().optional(),
})

const applicantsSchema = z.object({
  dataFormat: z.string().optional(),
  loadSource: z.string().optional(),
  organizationType: z.string().optional(),
  partyName: z.string().optional(),
  partyNameClean: z.string().optional(),
  partyType: z.string().optional(),
  sequence: z.number().optional(),
})

const ipcClassificationSchema = z.object({
  actionDate: z.string().optional(),
  classificationLevel: z.string().optional(),
  classificationSource: z.string().optional(),
  classificationValue: z.string().optional(),
  edition: z.number().optional(),
  generatingOffice: z.string().optional(),
  loadSource: z.string().optional(),
  mainGroup: z.string().optional(),
  originalReclassified: z.string().optional(),
  position: z.string().optional(),
  section: z.string().optional(),
  sequence: z.number().optional(),
  subClass: z.string().optional(),
  subGroup: z.string().optional(),
  symbol: z.string().optional(),
  topClass: z.string().optional(),
  versionDate: z.string().optional(),
})

const ktminePatentSchema = z.object({
  abstractParagraphs: z.array(abstractParagraphSchema).optional(),
  applicants: z.array(applicantsSchema).optional(),
  applicationReferences: z.array(applicationReferencesSchema).optional(),
  applicationReference: applicationReferencesSchema.optional(),
  backwardCitations: z.array(citationSchema).optional(),
  claims: z.array(claimSchema).optional(),
  claimsXml: z.array(claimsXmlSchema).optional(),
  cpcClassifications: z.array(cpcClassificationSchema).optional(),
  currentAssignees: z.array(currentAssigneeSchema).optional(),
  currentOwners: z.array(currentOwnerSchema).optional(),
  dateProduced: z.string().optional(),
  descriptions: z.array(descriptionSchema).optional(),
  documentNumber: z.string(),
  familyId: z.string().optional(),
  forwardCitations: z.array(citationSchema).optional(),
  imagePaths: z.array(z.string()).optional(),
  inventionTitle: z.string().optional(),
  inventionTitles: z.array(inventionTitlesSchema).optional(),
  inventors: z.array(inventorsSchema).optional(),
  ipcClassifications: z.array(ipcClassificationSchema).optional(),
  isLicensed: z.boolean().optional(),
  isLitigated: z.boolean().optional(),
  legalEvents: z.array(legalEventsSchema).optional(),
  legalStatus: z.string().optional(),
  jsonPath: z.string().optional(),
  minPriorityDate: z.string().optional(),
  pdfPath: z.string().optional(),
  priorityClaims: z.array(priorityClaimsSchema).optional(),
  projectedExpirationDate: z.string().optional(),
  publicationReference: publicationReferenceSchema,
  publicationReferences: z.array(publicationReferenceSchema).optional(),
  publicationType: z.string().optional(),
  xmlPath: z.string().optional(),
})

export const ktminePatentArraySchema = z.array(ktminePatentSchema)

export type KTMinePatentDetails = z.infer<typeof ktminePatentSchema>
